import { MeterEventEntity, type SerializedMeterEvent } from '@enmon/commons/lib/tools';
import type { ColumnsType } from 'antd/lib/table';
import { FormattedDateTime, Table } from 'components';
import type { Sorter } from 'components/table/types/sorter';
import { transformSorters } from 'components/table/utils/transformSorters';
import { TruncateText } from 'components/TruncateText';
import { type FC, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMeterEvents } from 'api/hooks/meter-events/useMeterEvents';
import { useTablePagination } from '../../../hooks/useTablePagination';
import { METER_EVENTS_TRANSLATED } from '../../portfolio/meters/routes/detail/routes/timeline/components/meter-event/constants';

// TODO: Fetch enmon meters and use relevant fields
//       (https://enmontech.atlassian.net/browse/ENMON-4522)
const getColumns = (
  activeMeterEventsTab: MeterEventEntity,
  sorts?: readonly Sorter[],
): ColumnsType<SerializedMeterEvent> => [
  ...(activeMeterEventsTab === MeterEventEntity.BackendGatewayMeter
    ? ([
        {
          title: 'Backend Gateway Meter ID',
          key: 'backend_gateway_meter_id',
          sorter: true,
          sortOrder: sorts?.find(sort => sort.field === 'backend_gateway_meter_id')?.order,
          render: (_, { backend_gateway_meter_id }) => (
            <Link to={`/portfolio/meters/${backend_gateway_meter_id}`}>
              <TruncateText maxLength={8} text={backend_gateway_meter_id} />
            </Link>
          ),
        },
      ] satisfies ColumnsType<SerializedMeterEvent>)
    : ([
        {
          title: 'Inventory Meter ID',
          key: 'inventory_meter.id',
          sorter: true,
          sortOrder: sorts?.find(sort => sort.field === 'inventory_meter.id')?.order,
          render: (_, { inventory_meter }) => (
            <Link to={`/portfolio/meters/${inventory_meter?.id}`}>{inventory_meter?.id}</Link>
          ),
        },
      ] satisfies ColumnsType<SerializedMeterEvent>)),
  {
    title: 'Type',
    key: 'type',
    sorter: true,
    sortOrder: sorts?.find(sort => sort.field === 'type')?.order,
    render: (_, { type }) => METER_EVENTS_TRANSLATED[type] || type,
  },
  {
    title: 'Submitted by',
    key: 'submitted_by.email',
    sorter: true,
    sortOrder: sorts?.find(sort => sort.field === 'submitted_by.email')?.order,
    render: (_, { submitted_by }) => <>{submitted_by?.email}</>,
  },
  {
    title: 'Submitted at',
    key: 'createdAt',
    sorter: true,
    sortOrder: sorts?.find(sort => sort.field === 'createdAt')?.order,
    render: (_, { createdAt }) => <FormattedDateTime date={createdAt} />,
  },
];

export const MeterEventsTable: FC<{ activeMeterEventsTab: MeterEventEntity }> = ({ activeMeterEventsTab }) => {
  const { paginationOptions, setPaginationOptions } = useTablePagination(0, 'meters events', 1, 20);
  const [sorts, setSorts] = useState<readonly Sorter[]>([{ field: 'createdAt', order: 'descend' }]);

  const entity =
    activeMeterEventsTab === MeterEventEntity.BackendGatewayMeter
      ? MeterEventEntity.BackendGatewayMeter
      : MeterEventEntity.InventoryMeter;

  const sortParams = sorts.reduce(
    (acc, sort) => {
      if (sort.order) {
        acc[sort.field] = sort.order === 'ascend' ? 'asc' : 'desc';
      }
      return acc;
    },
    {} as Record<string, 'asc' | 'desc'>,
  );

  const { data: meterEvents, isLoading: isLoadingMeterEvents } = useMeterEvents({
    entity,
    page: paginationOptions.current,
    pageSize: paginationOptions.pageSize,
    ...(Object.keys(sortParams).length > 0 && { sort: sortParams }),
  });

  return (
    <Table<SerializedMeterEvent>
      columns={getColumns(activeMeterEventsTab, sorts)}
      dataSource={meterEvents?.data || []}
      loading={isLoadingMeterEvents}
      pagination={{
        ...paginationOptions,
        total: meterEvents?.meta.pagination.total || 0,
        onChange: (page, pageSize) => {
          setPaginationOptions({ currentPage: page, pageSize });
        },
      }}
      rowKey="id"
      scroll={{ y: 800 }}
      onChange={(_, __, sorters, { action }) => {
        switch (action) {
          case 'sort': {
            const newSorts = transformSorters(sorts ?? [], [sorters].flat());
            setSorts(newSorts);
            break;
          }
          default:
            console.warn('Unhandled table action', action);
        }
      }}
    />
  );
};
